import { ToastType } from 'modules/application/misc/ToastService';
import { ConfirmationModal } from 'modules/ui';
import moment from 'moment';
import { ConfigurationFeatures } from '../ConfigurationFeatures';
import { CONFIGURATIONS_KEY_INFO_OPENED, CONFIGURATIONS_KEY_ITEM_CREATED, CONFIGURATIONS_KEY_ITEM_DELETED, CONFIGURATIONS_KEY_VIEW_OPENED, } from '../ConfigurationsSegmentEvents';
import { CreatedKeyModalController } from './CreatedKeyModal/CreatedKeyModalController';
import CreatedKeyModalView from './CreatedKeyModal/CreatedKeyModalView.html';
import './Keys.scss';
const ADMIN_MSGING_IDENTITY = 'admin@msging.net';
const ADMIN_BLIP_IDENTITY = 'admin@blip.ai';
const ADMIN_OMNI_IDENTITY = 'admin@0mn.io';
const CERBERUS_COMMANDS_MSGING_IDENTITY = 'cerberuscommands@msging.net';
const CERBERUS_COMMANDS_BLIP_IDENTITY = 'cerberuscommands@blip.ai';
export class KeysController {
    static $inject = ['ModalService', 'ApplicationService2', 'BlipToastService', 'SegmentService', '$state', '$translate', 'application'];
    constructor(ModalService, ApplicationService2, BlipToastService, SegmentService, $state, $translate, application) {
        'ngInject';
        this.ModalService = ModalService;
        this.ApplicationService2 = ApplicationService2;
        this.BlipToastService = BlipToastService;
        this.SegmentService = SegmentService;
        this.$state = $state;
        this.$translate = $translate;
        this.application = application;
        this.MAX_TOKENS = 3;
        this.onChangePurpose = (event) => {
            this.formsPurpose = event.detail?.value;
        };
        this.keys = [];
        this.showInfo = false;
        this.showCreationModal = false;
        this.formsPurpose = '';
        this.isCreatingKey = false;
    }
    async $onInit() {
        const isTokenManagementEnable = await ConfigurationFeatures.isTokenManagementEnable();
        if (!isTokenManagementEnable) {
            this.$state.go('auth.application.list');
        }
        await this.trackViewOpened();
        await this.query();
    }
    async trackViewOpened() {
        this.SegmentService.createApplicationTrack({
            trackEvent: CONFIGURATIONS_KEY_VIEW_OPENED,
        });
    }
    async query() {
        this.isLoading = true;
        this.keys = [];
        try {
            const { items } = await this.ApplicationService2.getAccessKeys();
            this.keys = items
                .filter(this.filterAdminRequirer)
                .map(this.parseKeyItem);
        }
        catch (error) {
            this.BlipToastService.show(ToastType.Danger, {
                msg: await this.$translate('tokens.errorMsg.queryError'),
            });
        }
        finally {
            this.isLoading = false;
        }
    }
    filterAdminRequirer(item) {
        if (!item.requirer) {
            return true;
        }
        const [identity, _] = item.requirer.split('/');
        const adminIdentities = [ADMIN_MSGING_IDENTITY, ADMIN_BLIP_IDENTITY, ADMIN_OMNI_IDENTITY, CERBERUS_COMMANDS_BLIP_IDENTITY, CERBERUS_COMMANDS_MSGING_IDENTITY];
        const createdByAdmin = identity && adminIdentities.includes(decodeURIComponent(identity));
        return !createdByAdmin;
    }
    parseKeyItem(item, index) {
        const purpose = item.purpose ? item.purpose : '-';
        const creationDate = item.creationDate
            ? moment(item.creationDate).format('L - HH:mm')
            : '-';
        const requirer = item.requirer && !item.requirer.startsWith('postmaster')
            ? decodeURIComponent(item.requirer.slice(0, item.requirer.indexOf('@')))
            : '-';
        return {
            id: item.id,
            purpose,
            creationDate,
            isDefault: index === 0,
            requirer,
        };
    }
    async createKey() {
        if (this.isCreatingKey) {
            return;
        }
        this.isCreatingKey = true;
        if (this.keys.length >= this.MAX_TOKENS) {
            this.BlipToastService.show(ToastType.Danger, {
                msg: await this.$translate('tokens.errorMsg.tokenLimitReached', { value: this.MAX_TOKENS }),
            });
            this.isCreatingKey = false;
            return;
        }
        if (!this.formsPurpose || this.formsPurpose === '') {
            this.BlipToastService.show(ToastType.Warning, {
                msg: await this.$translate('tokens.errorMsg.tokenNameRequired'),
            });
            this.isCreatingKey = false;
            return;
        }
        let success = false;
        let key;
        try {
            key = await this.ApplicationService2.setAccessKeys(this.formsPurpose);
            await this.ApplicationService2.setApplicationAccessKey(this.application.shortName, key.key);
            success = true;
            this.BlipToastService.show(ToastType.Success, {
                msg: await this.$translate('tokens.successMsg.tokenCreated'),
            });
            this.ModalService.showModal({
                template: CreatedKeyModalView,
                controller: CreatedKeyModalController,
                controllerAs: '$ctrl',
                inputs: {
                    key,
                    sdkkey: key.key,
                    shortName: this.application.shortName,
                },
            });
        }
        catch (error) {
            this.BlipToastService.show(ToastType.Danger, {
                msg: await this.$translate('tokens.errorMsg.createError'),
            });
        }
        finally {
            this.SegmentService.createApplicationTrack({
                trackEvent: CONFIGURATIONS_KEY_ITEM_CREATED,
                payload: {
                    keyId: key.id,
                    success,
                },
            });
        }
        this.closeCreationModal();
        this.isCreatingKey = false;
        this.query();
    }
    async deleteKey(key) {
        if (key.isDefault) {
            this.BlipToastService.show(ToastType.Danger, {
                msg: await this.$translate('tokens.errorMsg.cantDeleteDefault'),
            });
            return;
        }
        if (!(await this.confirmDeleteKey(key))) {
            return;
        }
        let success = false;
        try {
            await this.ApplicationService2.deleteAccessKey(key.id);
            success = true;
            this.BlipToastService.show(ToastType.Success, {
                msg: await this.$translate('tokens.successMsg.tokenDeleted'),
            });
        }
        finally {
            this.SegmentService.createApplicationTrack({
                trackEvent: CONFIGURATIONS_KEY_ITEM_DELETED,
                payload: {
                    keyId: key.id,
                    success,
                },
            });
        }
        this.query();
    }
    async confirmDeleteKey(key) {
        const modalTitle = await this.$translate('tokens.deleteBox.title');
        const warning = await this.$translate('tokens.warning.httpSessionDeleteKey');
        const modalBody = await this.$translate('tokens.deleteBox.text', {
            name: key.purpose,
        });
        const deleteText = await this.$translate('utils.forms.delete');
        const returnText = await this.$translate('utils.forms.return');
        const modal = await this.ModalService.showModal(ConfirmationModal({
            title: {
                text: modalTitle,
            },
            body: `<p>${warning}</p><p>${modalBody}</p>`,
            warning: warning,
            buttons: {
                confirm: { text: deleteText },
                cancel: { text: returnText },
            },
        }));
        const confirmation = await modal.close;
        return confirmation;
    }
    openCreationModal() {
        this.showCreationModal = true;
    }
    closeCreationModal() {
        this.showCreationModal = false;
        this.formsPurpose = '';
    }
    toggleInfo() {
        this.showInfo = !this.showInfo;
        if (this.showInfo) {
            this.SegmentService.createApplicationTrack({
                trackEvent: CONFIGURATIONS_KEY_INFO_OPENED,
            });
        }
    }
}
KeysController.$inject = ['ModalService', 'ApplicationService2', 'BlipToastService', 'SegmentService', '$state', '$translate', 'application'];
